/**
 * 배경 그림에서 **벽 초안**을 뽑는다 — 단일 정의.
 *
 * 관리자 버튼(브라우저 캔버스)과 `scripts/collision-draft.mjs`(sharp)가 **같은 함수**를 쓴다.
 * 두 벌로 두면 반드시 갈린다 — 운영자가 버튼으로 만든 결과와 스크립트 결과가 다르면
 * 어느 쪽이 맞는지 아무도 모르게 된다.
 *
 * ## 무엇을 재는가
 * 밝기가 강한 신호다(실측: 어두운 칸 89~100% 막힘, 밝은 칸 25%). 물은 파랑으로 거른다.
 *
 * ## 무엇을 못 재는가
 * **밝기는 "빛을 받는가"를 재지 "밟을 수 있는가"를 재지 않는다.** 달빛 받은 석탑과
 * 달빛 받은 흙길은 밝기가 같다. 그래서 밝은 돌 구조물(석탑·석상·계단)은 길로 오인된다.
 * 이건 규칙으로 못 고친다 — **사람이 보고 붓으로 고쳐야 한다.** 그래서 이 함수의 결과는
 * 언제나 "초안"이고, 그대로 게시하지 않는다.
 *
 * ## 뜻밖에 잘 드는 규칙
 * "걸어서 못 가는 열린 구역은 전부 닫는다" — 걸어갈 수 없는 곳을 열어 두면 아무 소용이 없고
 * NPC 가 그리로 걸어가 갇힌다. 실제로 이 규칙이 도서관 병풍(99조각)과 굿즈 좌판(120조각)을
 * 자동으로 잡았다. 밝아서 길로 오인됐지만 어디서도 닿을 수 없는 곳이었다.
 */

export interface WallScanOptions {
  /**
   * 밝기 경계를 직접 지정(0~255). 없으면 **오츠(Otsu) 방법**으로 그림에서 자동으로 찾는다.
   *
   * 예전엔 "밝기 상위 45%를 길로" 같은 분위수를 썼는데, 그건 **그림의 절반쯤이 길이라고
   * 강제**하는 것이라 대부분이 마당인 존을 표현할 수 없었다(실내는 0.45, 야외는 0.55 로
   * 존마다 손으로 맞춰야 했다). 오츠는 밝은 무리와 어두운 무리의 자연스러운 경계를 찾아
   * 그림이 알아서 말하게 한다.
   */
  threshold?: number;
  /** 조각 안 밝은 픽셀이 이 비율을 넘으면 길. */
  brightFraction?: number;
  /** 파란 픽셀이 이 비율을 넘으면 물 → 막음. */
  blueFraction?: number;
  /** 이보다 작은 덩어리는 주변에 맞춘다(잔티끌 제거). */
  speckle?: number;
}

export interface WallScanResult {
  /** 길이 cols*rows. 타일당 조각 비트마스크(막힌 조각 = 1). `collision_fine` 에 그대로 넣는다. */
  fine: number[];
  /** 걸을 수 있는 조각 수 / 전체. */
  openRatio: number;
  /** 잔티끌로 뒤집힌 조각 수. */
  speckle: number;
  /** 걸어서 못 가서 닫은 조각 수. */
  islands: number;
}

const DEFAULTS: Required<Omit<WallScanOptions, "threshold">> = {
  brightFraction: 0.5,
  blueFraction: 0.35,
  speckle: 4,
};

/**
 * 오츠(Otsu) 방법 — 밝기 히스토그램에서 두 무리를 가장 잘 가르는 경계를 찾는다.
 * 그림이 대부분 길이든 대부분 건물이든 알아서 맞는다(분위수는 비율을 강제해서 못 그런다).
 */
export function otsuThreshold(lum: Float32Array): number {
  const hist = new Float64Array(256);
  for (let i = 0; i < lum.length; i++) hist[Math.max(0, Math.min(255, Math.round(lum[i]!)))]! += 1;
  const total = lum.length;
  let sum = 0;
  for (let t = 0; t < 256; t++) sum += t * hist[t]!;
  let sumB = 0;
  let wB = 0;
  let best = 0;
  let bestVar = -1;
  for (let t = 0; t < 256; t++) {
    wB += hist[t]!;
    if (wB === 0) continue;
    const wF = total - wB;
    if (wF === 0) break;
    sumB += t * hist[t]!;
    const mB = sumB / wB;
    const mF = (sum - sumB) / wF;
    const between = wB * wF * (mB - mF) * (mB - mF);
    if (between > bestVar) {
      bestVar = between;
      best = t;
    }
  }
  return best;
}

/** 이어진 덩어리들(4방향). */
function components(mask: Uint8Array, W: number, H: number, target: number): number[][] {
  const seen = new Uint8Array(W * H);
  const out: number[][] = [];
  for (let i = 0; i < W * H; i++) {
    if (seen[i] || mask[i] !== target) continue;
    const stack = [i];
    const group: number[] = [];
    seen[i] = 1;
    while (stack.length) {
      const k = stack.pop()!;
      group.push(k);
      const x = k % W;
      const y = (k / W) | 0;
      const push = (nk: number) => {
        if (seen[nk] || mask[nk] !== target) return;
        seen[nk] = 1;
        stack.push(nk);
      };
      if (x > 0) push(k - 1);
      if (x < W - 1) push(k + 1);
      if (y > 0) push(k - W);
      if (y < H - 1) push(k + W);
    }
    out.push(group);
  }
  return out;
}

/**
 * @param data 픽셀. **맵 픽셀 크기로 cover-fit 해서 자른 뒤**의 것이어야 한다 —
 *             월드가 배경을 그렇게 놓기 때문에, 다르게 넣으면 벽이 통째로 밀린다.
 * @param channels 3(RGB) 또는 4(RGBA)
 */
export function scanWallsFromImage(
  data: Uint8Array | Uint8ClampedArray,
  channels: 3 | 4,
  imgW: number,
  imgH: number,
  cols: number,
  rows: number,
  div: number,
  options: WallScanOptions = {},
): WallScanResult {
  const o = { ...DEFAULTS, ...options };
  const n = imgW * imgH;
  const lum = new Float32Array(n);
  const blue = new Uint8Array(n);
  for (let i = 0; i < n; i++) {
    const r = data[i * channels]!;
    const g = data[i * channels + 1]!;
    const b = data[i * channels + 2]!;
    lum[i] = 0.299 * r + 0.587 * g + 0.114 * b;
    blue[i] = b > r * 1.12 && b > 60 ? 1 : 0; // 물·밤하늘
  }
  const thr = options.threshold ?? otsuThreshold(lum);

  const cellW = imgW / (cols * div);
  const cellH = imgH / (rows * div);
  const SW = cols * div;
  const SH = rows * div;
  const walk = new Uint8Array(SW * SH);
  for (let sy = 0; sy < SH; sy++) {
    for (let sx = 0; sx < SW; sx++) {
      let bright = 0;
      let bl = 0;
      let cnt = 0;
      const x0 = Math.floor(sx * cellW);
      const x1 = Math.max(x0 + 1, Math.floor((sx + 1) * cellW));
      const y0 = Math.floor(sy * cellH);
      const y1 = Math.max(y0 + 1, Math.floor((sy + 1) * cellH));
      for (let y = y0; y < y1 && y < imgH; y++) {
        for (let x = x0; x < x1 && x < imgW; x++) {
          cnt++;
          if (lum[y * imgW + x]! > thr) bright++;
          if (blue[y * imgW + x]) bl++;
        }
      }
      walk[sy * SW + sx] = cnt > 0 && bright / cnt > o.brightFraction && bl / cnt < o.blueFraction ? 1 : 0;
    }
  }

  // 잔티끌 제거 — 한두 조각짜리 구멍·점은 그림의 얼룩이지 벽이 아니다
  let speckle = 0;
  for (const target of [1, 0] as const) {
    for (const g of components(walk, SW, SH, target)) {
      if (g.length <= o.speckle) {
        for (const k of g) walk[k] = target ? 0 : 1;
        speckle += g.length;
      }
    }
  }

  // 걸어서 못 가는 열린 구역은 전부 닫는다(가장 큰 구역만 남긴다)
  const opens = components(walk, SW, SH, 1).sort((a, b) => b.length - a.length);
  let islands = 0;
  for (const g of opens.slice(1)) {
    for (const k of g) walk[k] = 0;
    islands += g.length;
  }

  const fine: number[] = [];
  for (let gy = 0; gy < rows; gy++) {
    for (let gx = 0; gx < cols; gx++) {
      let mask = 0;
      for (let sy = 0; sy < div; sy++) {
        for (let sx = 0; sx < div; sx++) {
          if (!walk[(gy * div + sy) * SW + gx * div + sx]) mask |= 1 << (sy * div + sx);
        }
      }
      fine.push(mask);
    }
  }
  const open = walk.reduce((s: number, v) => s + v, 0);
  return { fine, openRatio: open / (SW * SH), speckle, islands };
}
